import { defaultHandlePlayerClick, getIconAsBgImage } from '@sportal365/smp-widget-utils';
import { SMPText, ConfigContext } from '@velinsi/smp-widgets-ui';
import { FC, useContext } from 'react';
import { formatPlayerName } from '../../utils/nameFormatters';
import { NameFormat } from '../../../../../packages/types/football';
import { PLAYER_ENTITY } from '../../../../../packages/constants/entityTypes';
import { MISS } from '../../../../../packages/constants/playerType';
import { GenericAlign, GenericSize } from '../../../../../packages/constants/generic';
import { ThumbComponent } from './Thumb';
import { styled } from '../../../../../packages/theme/stitches.config';
import { EventTypeCode, ICoach, IMatchEvent, IMatchPlayerLineup, IPlayer, ISubscriberInfo, IType } from '../../../../../packages/types';
import LinkServiceContext from '../../contexts/LinkServiceContext';
import EventIcons, { EventIcon, EventIconsWrapper } from './EventIcons';

export interface ParticipantProps {
  data: Pick<IPlayer | ICoach, 'id' | 'name' | 'slug' | 'assets'> | undefined;
  entityType: string;
  shirtColor?: string;
  shirtNumber?: number;
  events?: IMatchPlayerLineup['events'];
  formation?: string;
  type?: IType;
  handleClick: (info: ISubscriberInfo, clickType: string) => void;
}

const getSubstitution = (playerId: string, events: IMatchEvent[]) => {
  const substitutionEvent = events.find(({ typeCode }) => typeCode === EventTypeCode.SUBSTITUTION);

  if (!substitutionEvent) {
    return undefined;
  }

  if (substitutionEvent.primaryPlayer.id === playerId) {
    return {
      minute: substitutionEvent.minute,
      name: substitutionEvent.secondaryPlayer.name,
    };
  }

  return {
    minute: substitutionEvent.minute,
    name: substitutionEvent.primaryPlayer.name,
  };
};

const LineupsRow: FC<{
  participants: (ParticipantProps | undefined)[];
  isEvenRow?: boolean;
}> = ({ participants, isEvenRow }) => {
  return (
    <RowWrapper css={isEvenRow ? { backgroundColor: '$lineupsRowEvenBgColor' } : { backgroundColor: '$lineupsRowOddBgColor' }}>
      {participants[0] && <Participant {...participants[0]} align={GenericAlign.LEFT} />}
      {participants[1] && <Participant {...participants[1]} align={GenericAlign.RIGHT} />}
    </RowWrapper>
  );
};

const Participant: FC<ParticipantProps & { align: GenericAlign }> = ({
  data,
  entityType,
  align,
  shirtNumber,
  shirtColor,
  events,
  formation,
  type,
  handleClick,
}) => {
  const { linkService, urlTarget } = useContext(LinkServiceContext);
  const { dataNameFormat } = useContext(ConfigContext);

  if (!data) {
    return null;
  }

  const { id, name, slug, assets } = data;
  const substitution = entityType === PLAYER_ENTITY && events ? getSubstitution(String(id), events) : undefined;
  const miss = entityType === PLAYER_ENTITY && type?.category === MISS ? type.name : undefined;

  const handlePlayerClick = () => defaultHandlePlayerClick(handleClick, data.id, data.name);

  return (
    <ParticipantWrapper
      align={align}
      href={linkService?.createEntityUrl(String(id), entityType, '', slug) ?? '#'}
      target={urlTarget}
      onClick={handlePlayerClick}
    >
      <SMPText
        display={entityType === PLAYER_ENTITY ? 'flex' : 'none'}
        size='4'
        variant='contrast'
        css={{ justifyContent: align, width: '$4', color: '$gray6' }}
      >
        {shirtNumber ?? '-'}
      </SMPText>
      <ThumbComponent imageUrl={assets.thumb?.url} borderColor={shirtColor || ''} size={GenericSize.SMALL} context='default' />
      <NameWrapper>
        <SMPText size='4' variant='contrast' textAlign={align}>
          {name}
        </SMPText>
        {(substitution || formation || miss) && (
          <AdditionalInfoWrapper align={align}>
            {substitution && (
              <>
                <EventIcon style={{ backgroundImage: `url("${getIconAsBgImage('substitution')}")` }} />
                <SMPText size='2_5' variant='contrast' css={{ color: '$lineupsSecondaryTextColor', lineHeight: '10px' }}>
                  {substitution.minute + "' " + formatPlayerName(substitution.name, dataNameFormat as NameFormat)}
                </SMPText>
              </>
            )}
            {miss && (
              <SMPText size='2_5' variant='contrast' css={{ color: '$lineupsSecondaryTextColor', lineHeight: '10px' }}>
                {miss}
              </SMPText>
            )}
          </AdditionalInfoWrapper>
        )}
      </NameWrapper>
      {events && <EventIcons events={events} displaySubstitution={false} />}
    </ParticipantWrapper>
  );
};

const RowWrapper = styled.withConfig({ componentId: 'LineupsRow', displayName: 'RowWrapper' })('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '54px',
});

const ParticipantWrapper = styled.withConfig({ componentId: 'Participant', displayName: 'ParticipantWrapper' })('a', {
  display: 'flex',
  gap: '$2',
  width: '50%',
  padding: '$3',
  textDecoration: 'none',
  outline: 'none',
  color: 'unset',

  '&:only-child': {
    width: '100%',
  },

  variants: {
    align: {
      left: {
        flexDirection: 'row',
      },
      right: {
        flexDirection: 'row-reverse',
        marginLeft: 'auto',

        [`${EventIconsWrapper}`]: {
          flexDirection: 'row-reverse',
        },
      },
    },
  },
});

const NameWrapper = styled.withConfig({ componentId: 'Participant', displayName: 'NameWrapper' })('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});

const AdditionalInfoWrapper = styled.withConfig({ componentId: 'Participant', displayName: 'AdditionalInfoWrapper' })('div', {
  display: 'flex',
  alignItems: 'center',
  padding: '0',
  gap: '8px',
  marginTop: '$0',

  variants: {
    align: {
      left: {
        flexDirection: 'row',
      },
      right: {
        flexDirection: 'row-reverse',
      },
    },
  },

  [`${SMPText}`]: {
    opacity: 0.7,
  },
});

export default LineupsRow;
